import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Download, Printer } from 'lucide-react';
import { ColumnDef, DataTable } from '../../Components/Table/DataTable';
import { InventoryBar } from '../../Components/Vending/InventoryBar';
import { MachineDetailHeader } from '../../Components/Vending/MachineDetailHeader';
import { MachineAlertsTab } from '../../Components/Vending/MachineAlertsTab';
import { MachineHistoryTab } from '../../Components/Vending/MachineHistoryTab';
import { MachineOverviewTab } from '../../Components/Vending/MachineOverviewTab';
import { MachineUsersTab } from '../../Components/Vending/MachineUsersTab';
import { StockLevelChip } from '../../Components/Vending/MachineStatusChip';
import { FilterChipRow, RestockDialogLine } from '../../Components/Vending/VendingStyles';
import vendingApiClient from '../../Services/VendingApi';
import { formatUTC } from '../../Services/FormatUtc';
import { PageContainer } from '../../Styles/AppStyles';
import { COLORS } from '../../Styles/Colors';
import {
  VendingInventoryResponse,
  VendingMachineDetail,
  VendingMachineDetailResponse,
  VendingTransactionListResponse,
  VendingTransactionSummary,
} from '../../Types/vending';

type InventoryFilter = 'all' | 'good' | 'low' | 'empty';

type InventoryTableRow = VendingInventoryResponse['data']['slots'][number] & {
  inventory_level: number;
};

const formatTxnDate = (value: string | null | undefined) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const txnStatusChip = (status: string | null | undefined) => {
  const value = (status || 'unknown').toLowerCase();
  let color: string = COLORS.lightGray;
  let bg = `${COLORS.lightGray}18`;
  if (value.includes('success') || value === 'ok' || value === 'complete' || value === 'completed') {
    color = COLORS.success;
    bg = `${COLORS.success}18`;
  } else if (value.includes('fail') || value.includes('error') || value.includes('cancel')) {
    color = COLORS.red;
    bg = `${COLORS.red}12`;
  } else if (value.includes('pend') || value.includes('process')) {
    color = COLORS.orange;
    bg = `${COLORS.orange}18`;
  }
  return (
    <Chip
      size="small"
      label={status || '—'}
      sx={{
        height: 22,
        fontSize: '0.7rem',
        fontWeight: 600,
        textTransform: 'capitalize',
        bgcolor: bg,
        color,
      }}
    />
  );
};

interface PickListLine {
  slot_id: number;
  location_key: string | null;
  stock_level: string;
  qty_to_bring: number;
  product: {
    name: string | null;
    provider_sku?: string | null;
    provider_item_id?: string | null;
  } | null;
}

export const VendingMachineDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const tab = useMemo(() => {
    if (location.pathname.endsWith('/inventory')) return 1;
    if (location.pathname.endsWith('/transactions')) return 2;
    if (location.pathname.endsWith('/alerts')) return 3;
    if (location.pathname.endsWith('/users')) return 4;
    if (location.pathname.endsWith('/history')) return 5;
    return 0;
  }, [location.pathname]);
  const [userCount, setUserCount] = useState<number | null>(null);
  const [machine, setMachine] = useState<VendingMachineDetail | null>(null);
  const [inventoryAsOf, setInventoryAsOf] = useState<string | null>(null);
  const [slots, setSlots] = useState<VendingInventoryResponse['data']['slots']>([]);
  const [transactions, setTransactions] = useState<VendingTransactionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inventoryFilter, setInventoryFilter] = useState<InventoryFilter>('all');
  const [pickListOpen, setPickListOpen] = useState(false);
  const [pickListLines, setPickListLines] = useState<PickListLine[]>([]);
  const [pickListLoading, setPickListLoading] = useState(false);

  const loadMachine = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);

    try {
      const [machineRes, inventoryRes, txRes] = await Promise.all([
        vendingApiClient(`/machines/${id}`),
        vendingApiClient(`/machines/${id}/inventory`),
        vendingApiClient(`/machines/${id}/transactions?limit=50`),
      ]);

      const machineJson: VendingMachineDetailResponse = await machineRes.json();
      const inventoryJson: VendingInventoryResponse = await inventoryRes.json();
      const txJson: VendingTransactionListResponse = await txRes.json();

      setMachine(machineJson.data);
      setSlots(inventoryJson.data.slots);
      setInventoryAsOf(inventoryJson.meta?.as_of ?? null);
      setTransactions(txJson.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load machine');
      setMachine(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadMachine();
  }, [loadMachine]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    vendingApiClient(`/machines/${id}/users?limit=1`)
      .then(async (response) => {
        const json = await response.json();
        if (!cancelled) setUserCount(json.meta?.total ?? json.data?.length ?? 0);
      })
      .catch(() => {
        if (!cancelled) setUserCount(null);
      });
    return () => { cancelled = true; };
  }, [id]);

  const inventoryCounts = useMemo(() => slots.reduce(
    (acc, slot) => {
      acc.all += 1;
      if (slot.stock_level === 'good') acc.good += 1;
      else if (slot.stock_level === 'low') acc.low += 1;
      else if (slot.stock_level === 'empty') acc.empty += 1;
      return acc;
    },
    { all: 0, good: 0, low: 0, empty: 0 },
  ), [slots]);

  const filteredSlots = useMemo(() => {
    if (inventoryFilter === 'all') return slots;
    return slots.filter((s) => s.stock_level === inventoryFilter);
  }, [slots, inventoryFilter]);

  const inventoryFilterOptions: { value: InventoryFilter; label: string; count: number }[] = [
    { value: 'all', label: 'All', count: inventoryCounts.all },
    { value: 'good', label: 'Good', count: inventoryCounts.good },
    { value: 'low', label: 'Low', count: inventoryCounts.low },
    { value: 'empty', label: 'Empty', count: inventoryCounts.empty },
  ];

  const loadPickList = async () => {
    if (!id) return;
    setPickListLoading(true);
    try {
      const response = await vendingApiClient(`/restock/${id}/picklist`);
      const json = await response.json();
      setPickListLines(json.data?.lines ?? []);
      setPickListOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load restock pick list');
    } finally {
      setPickListLoading(false);
    }
  };

  const machineDisplayName = machine?.machine_name
    || (machine ? `VM-${machine.provider_machine_id}` : 'Machine');

  const exportPickListCsv = () => {
    const headers = ['Machine', 'Slot', 'SKU', 'Product', 'Stock Level', 'Qty'];
    const rows = pickListLines.map((line) => [
      machineDisplayName,
      line.location_key || line.slot_id,
      line.product?.provider_sku || '—',
      line.product?.name || 'Unknown product',
      line.stock_level,
      line.qty_to_bring,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vending-restock-${id}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrintPickList = () => {
    window.print();
  };

  const inventoryRows = useMemo(
    () => filteredSlots.map((s) => {
      const cap = s.capacity && s.capacity > 0 ? s.capacity : null;
      return {
        ...s,
        id: s.id,
        inventory_level: cap ? Math.round((s.current_quantity / cap) * 100) : -1,
      };
    }),
    [filteredSlots],
  );

  const inventoryColumns: ColumnDef<InventoryTableRow>[] = useMemo(() => [
    {
      field: 'product',
      headerName: 'Product',
      width: 260,
      minWidth: 200,
      searchable: true,
      valueGetter: (_v, row) => row.product?.name || '—',
      renderCell: (params) => (
        <Box sx={{ py: 0.5, width: '100%', minWidth: 0, lineHeight: 1.35 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              color: COLORS.darkGray,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.35,
            }}
          >
            {params.row.product?.name || '—'}
          </Typography>
          {params.row.product?.provider_sku && (
            <Typography variant="caption" sx={{ color: COLORS.lightGray, lineHeight: 1.3 }}>
              SKU {params.row.product.provider_sku}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'location_key',
      headerName: 'Slot',
      width: 90,
      searchable: true,
      valueGetter: (_v, row) => row.location_key || [row.tray, row.slot].filter(Boolean).join('-') || '—',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.teal, fontFamily: 'monospace' }}>
          {params.row.location_key || [params.row.tray, params.row.slot].filter(Boolean).join('-') || '—'}
        </Typography>
      ),
    },
    {
      field: 'current_quantity',
      headerName: 'Qty',
      width: 70,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', width: '100%' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'capacity',
      headerName: 'Capacity',
      width: 90,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ textAlign: 'right', width: '100%', color: COLORS.lightGray }}>
          {params.value ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'inventory_level',
      headerName: 'Level',
      width: 120,
      sortable: true,
      valueGetter: (_v, row) => row.inventory_level,
      renderCell: (params) => (
        <Box sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
          <InventoryBar
            current={params.row.current_quantity}
            capacity={params.row.capacity}
            stockLevel={params.row.stock_level}
          />
        </Box>
      ),
    },
    {
      field: 'stock_level',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => <StockLevelChip level={params.value} />,
    },
  ], []);

  const transactionColumns: ColumnDef<VendingTransactionSummary>[] = useMemo(() => [
    {
      field: 'vend_timestamp',
      headerName: 'Date',
      width: 180,
      minWidth: 160,
      valueGetter: (_v, row) => formatTxnDate(row.vend_timestamp),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: COLORS.darkGray, whiteSpace: 'nowrap' }}>
          {formatTxnDate(params.row.vend_timestamp)}
        </Typography>
      ),
    },
    {
      field: 'product_name',
      headerName: 'Product',
      width: 240,
      minWidth: 160,
      searchable: true,
      valueGetter: (_v, row) => row.product_name || '—',
      renderCell: (params) => (
        <Typography
          variant="body2"
          noWrap
          title={params.row.product_name || '—'}
          sx={{ maxWidth: '100%' }}
        >
          {params.row.product_name || '—'}
        </Typography>
      ),
    },
    {
      field: 'employee_name',
      headerName: 'User',
      width: 150,
      minWidth: 110,
      searchable: true,
      valueGetter: (_v, row) => row.employee_name || '—',
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.row.employee_name || '—'}>
          {params.row.employee_name || '—'}
        </Typography>
      ),
    },
    {
      field: 'location_key',
      headerName: 'Slot',
      width: 90,
      minWidth: 70,
      valueGetter: (_v, row) => row.location_key || '—',
    },
    {
      field: 'quantity',
      headerName: 'Qty',
      width: 80,
      minWidth: 70,
      type: 'number',
    },
    {
      field: 'unit_price',
      headerName: 'Unit price',
      width: 110,
      minWidth: 90,
      valueGetter: (_v, row) => (
        row.unit_price != null ? `$${Number(row.unit_price).toFixed(2)}` : '—'
      ),
    },
    {
      field: 'transaction_status',
      headerName: 'Status',
      width: 130,
      minWidth: 110,
      renderCell: (params) => txnStatusChip(params.row.transaction_status),
    },
  ], []);

  const handleTabChange = (_: React.SyntheticEvent, value: number) => {
    if (!id) return;
    const routes = [
      `/vending/machines/${id}`,
      `/vending/machines/${id}/inventory`,
      `/vending/machines/${id}/transactions`,
      `/vending/machines/${id}/alerts`,
      `/vending/machines/${id}/users`,
      `/vending/machines/${id}/history`,
    ];
    navigate(routes[value]);
  };

  const usersTabLabel = userCount != null ? `Users (${userCount})` : 'Users';
  const alertsTabLabel = `Alerts (${machine?.open_alerts_count ?? 0})`;

  return (
    <PageContainer>
      {machine && (
        <MachineDetailHeader
          machine={machine}
          onBack={() => navigate('/vending/machines')}
        />
      )}

      {!machine && !loading && (
        <Box sx={{ px: 3, pt: 2 }}>
          <Typography variant="h5" fontWeight="bold">Machine</Typography>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          px: { xs: 2, md: 3 },
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8, flexShrink: 0 }}>
            <CircularProgress sx={{ color: COLORS.teal }} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ my: 2, flexShrink: 0 }}>{error}</Alert>
        )}

        {!loading && machine && (
          <>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              sx={{
                mb: 2,
                flexShrink: 0,
                borderBottom: `2px solid ${COLORS.lightGray}35`,
                minHeight: 44,
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.9375rem',
                  minHeight: 44,
                  color: COLORS.lightGray,
                },
                '& .Mui-selected': { color: COLORS.teal },
                '& .MuiTabs-indicator': { backgroundColor: COLORS.teal, height: 3, borderRadius: '3px 3px 0 0' },
              }}
            >
              <Tab label="Overview" />
              <Tab label={`Inventory (${slots.length})`} />
              <Tab label={`Transactions (${transactions.length})`} />
              <Tab label={alertsTabLabel} />
              <Tab label={usersTabLabel} />
              <Tab label="History" />
            </Tabs>

            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {tab === 0 && (
                <MachineOverviewTab machine={machine} slots={slots} transactions={transactions} />
              )}

              {tab === 1 && (
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                      flexShrink: 0,
                    }}
                  >
                    <FilterChipRow>
                      <Typography
                        variant="body2"
                        sx={{ color: COLORS.lightGray, fontWeight: 600, mr: 0.5 }}
                      >
                        Show:
                      </Typography>
                      {inventoryFilterOptions.map((option) => (
                        <Chip
                          key={option.value}
                          label={`${option.label} (${option.count})`}
                          size="small"
                          clickable
                          onClick={() => setInventoryFilter(option.value)}
                          sx={{
                            fontWeight: 600,
                            backgroundColor: inventoryFilter === option.value ? COLORS.teal : `${COLORS.lightGray}20`,
                            color: inventoryFilter === option.value ? COLORS.white : COLORS.darkGray,
                            '&:hover': {
                              backgroundColor: inventoryFilter === option.value ? COLORS.teal : `${COLORS.teal}18`,
                            },
                          }}
                        />
                      ))}
                    </FilterChipRow>
                    <Button
                      variant="contained"
                      onClick={loadPickList}
                      disabled={pickListLoading}
                      sx={{
                        backgroundColor: COLORS.teal,
                        fontWeight: 600,
                        borderRadius: '6px',
                        px: 2.5,
                        flexShrink: 0,
                        '&:hover': { backgroundColor: COLORS.teal, transform: 'translateY(-2px)' },
                      }}
                    >
                      {pickListLoading ? 'Loading…' : 'Create Restock List'}
                    </Button>
                  </Box>
                  {inventoryAsOf && (
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, flexShrink: 0, color: COLORS.teal, fontWeight: 500 }}
                    >
                      Inventory as of {formatUTC(inventoryAsOf)} UTC
                    </Typography>
                  )}
                  <DataTable
                    columns={inventoryColumns}
                    data={inventoryRows}
                    pageKey={`vending-machine-${id}-inventory-v3-${inventoryFilter}`}
                    idField="id"
                    rowHeight={68}
                  />
                </Box>
              )}

              {tab === 2 && id && (
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  {transactions.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 8,
                        px: 2,
                        borderRadius: 2,
                        border: `1px dashed ${COLORS.lightGray}`,
                        backgroundColor: '#fafbfc',
                      }}
                    >
                      <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.darkGray, mb: 1 }}>
                        No transactions
                      </Typography>
                      <Typography variant="body2" sx={{ color: COLORS.lightGray }}>
                        No vend activity synced for this machine yet.
                      </Typography>
                    </Box>
                  ) : (
                    <DataTable
                      columns={transactionColumns}
                      data={transactions.map((t) => ({
                        ...t,
                        id: t.id,
                        product_name: t.product_name || '—',
                      }))}
                      pageKey={`vending-machine-${id}-transactions-v2`}
                      idField="id"
                      rowHeight={52}
                    />
                  )}
                </Box>
              )}

              {tab === 3 && id && <MachineAlertsTab machineId={id} />}

              {tab === 4 && id && <MachineUsersTab machineId={id} />}

              {tab === 5 && id && <MachineHistoryTab machineId={id} />}
            </Box>
          </>
        )}
      </Box>

      <Dialog
        open={pickListOpen}
        onClose={() => setPickListOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            border: `1px solid ${COLORS.lightGray}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            color: COLORS.darkGray,
            fontWeight: 700,
            fontSize: '1.5rem',
            pb: 1,
          }}
        >
          Restock Pick List
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 3 }}>
          {pickListLines.length === 0 ? (
            <Typography sx={{ py: 2, color: COLORS.lightGray, textAlign: 'center' }}>
              No products need restocking on this machine.
            </Typography>
          ) : (
            <>
              <Typography
                variant="body2"
                sx={{ color: COLORS.teal, fontWeight: 600, mb: 2, textAlign: 'center' }}
              >
                {machineDisplayName}
                {' · '}
                {pickListLines.length} product{pickListLines.length === 1 ? '' : 's'} to replenish
              </Typography>
              <Box id="machine-restock-print-area">
                {pickListLines.map((line) => (
                  <RestockDialogLine key={line.slot_id}>
                    <Box>
                      <Typography fontWeight={700} sx={{ color: COLORS.darkGray }}>
                        {line.product?.name || 'Unknown'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: COLORS.lightGray, mt: 0.25 }}>
                        {line.product?.provider_sku || '—'}
                        {' · Slot '}
                        {line.location_key || line.slot_id}
                        {' · '}
                        <Box component="span" sx={{ textTransform: 'capitalize' }}>
                          {line.stock_level}
                        </Box>
                      </Typography>
                    </Box>
                    <Typography fontWeight={700} sx={{ color: COLORS.teal, fontSize: '1.1rem', flexShrink: 0 }}>
                      Take {line.qty_to_bring}
                    </Typography>
                  </RestockDialogLine>
                ))}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Printer size={16} />}
                  onClick={handlePrintPickList}
                >
                  Print
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Download size={16} />}
                  onClick={exportPickListCsv}
                  sx={{ backgroundColor: COLORS.teal }}
                >
                  Export CSV
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default VendingMachineDetailPage;
